import { clearToken, getToken } from './auth'
import { API_BASE_URL as baseUrl } from './api-base-url'

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.'
const NETWORK_ERROR_MESSAGE =
  'Unable to connect right now. Please try again shortly.'
const SENSITIVE_ERROR_PATTERN =
  /\b(https?:\/\/|localhost|railway|stack|trace|token|secret|password|jwt|bearer|prisma|sql|database|exception)\b/i

type ApiError = {
  error?: {
    message?: string
    code?: string
    details?: unknown
  }
}

function sanitizeApiMessage(
  message: string | undefined,
  status?: number,
): string {
  const normalized = message?.trim()
  if (!normalized)
    return status && status < 500
      ? `Request failed (${status})`
      : GENERIC_ERROR_MESSAGE
  if (status && status >= 500) return GENERIC_ERROR_MESSAGE
  if (SENSITIVE_ERROR_PATTERN.test(normalized)) return GENERIC_ERROR_MESSAGE
  return normalized
}

export function createApiError(params: {
  message?: string
  status?: number
  code?: string
  details?: unknown
}): Error & { status?: number; code?: string; details?: unknown } {
  const error = new Error(
    sanitizeApiMessage(params.message, params.status),
  ) as Error & {
    status?: number
    code?: string
    details?: unknown
  }
  error.status = params.status
  error.code = params.code
  error.details = params.details
  return error
}

export async function apiRequest<T>(path: string, options: RequestInit = {}) {
  const headers = new Headers(options.headers || {})
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json')
  }

  const token = getToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  let res: Response
  try {
    res = await fetch(`${baseUrl}${path}`, {
      ...options,
      headers,
    })
  } catch (cause) {
    throw createApiError({
      message: NETWORK_ERROR_MESSAGE,
      code: 'API_UNREACHABLE',
      details: cause,
    })
  }

  if (res.status === 204) {
    return null as T
  }

  if (res.status === 401 || res.status === 403) {
    clearToken()
    const isLoginRoute = window.location.pathname.toLowerCase() === '/login'
    if (token && !isLoginRoute) {
      window.location.replace('/login')
    }
    throw createApiError({
      message: 'Session expired',
      status: res.status,
      code: 'UNAUTHORIZED',
    })
  }

  const text = await res.text()
  let data: unknown = null
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = null
    }
  }

  if (!res.ok) {
    const err = data as ApiError
    throw createApiError({
      message: err?.error?.message ?? `Request failed (${res.status})`,
      status: res.status,
      code: err?.error?.code,
      details: err?.error?.details,
    })
  }

  return data as T
}
